import { useCallback, useState } from 'react';
import { isEqual } from 'lodash';
import { useInterval } from './useInterval';

interface Offset {
  top: number;
  left: number;
  right: number;
  bottom: number;
}

const getOffset = (element: HTMLElement): Offset => {
  const { top, left, width, height } = element.getBoundingClientRect();

  return {
    top: top + window.scrollY,
    left: left + window.scrollX,
    right: left + window.scrollX + width,
    bottom: top + window.scrollY + height,
  };
};

/**
 * Create an empty element ref and keep track of the offset of an element
 * when it is assigned to a DOM element. The offset of an element is
 * updated anytime it changes position within the context of the page.
 *
 * @returns {{elementOffset: Offset, ref: React.RefCallback<HTMLElement>}}
 * A ref callback to be set to a React element and the offset of that element
 */
export const useElementOffset = (): {
  elementOffset: Offset | null;
  ref: React.RefCallback<HTMLElement>;
} => {
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [elementOffset, setElementOffset] = useState<Offset | null>(null);

  const callback = useCallback(() => {
    if (element != null) {
      setElementOffset(offset => {
        const updatedOffset = getOffset(element);
        return isEqual(offset, updatedOffset) ? offset : updatedOffset;
      });
    }
  }, [element]);

  useInterval(callback, 100);

  return { elementOffset, ref: setElement };
};
